import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import { UserCountryService } from '@app/public/shared/services/user-country.service';
import { LocalStorageService } from '@app/core/services/local-storage.service';
import { ContentType, CouponTypes, WalletType } from '@app/core/enums';
import { ApiResponse } from '@app/core/models/api-response.model';
import { Wallet } from '@app/core/interfaces';
import { AppConstant } from '@app/core/constant/app-constant';
import {
  AddMoneyPayload,
  WalletPayPayload,
} from '@app/core/interfaces/movie.interface';
import { MoviePurchaseResponse } from '@app/pages/shared/interfaces/movie.model';
import { PaymentGatewayService } from '@app/core/services/payment-gateway.service';

@Injectable({
  providedIn: 'root',
})
export class WalletService {
  baseUrl: string = environment.apiUrl;
  CELEBRATION_FLAG = '_celebrationFlag';

  constructor(
    private readonly httpClient: HttpClient,
    private readonly userCountryService: UserCountryService,
    private readonly localStorageService: LocalStorageService,
    private readonly paymentGatewayService: PaymentGatewayService,
  ) {}

  getBalance(): Observable<ApiResponse<Wallet>> {
    return this.httpClient.get<ApiResponse<Wallet>>(`${this.baseUrl}/wallet`);
  }

  getPromotionalCash(): Observable<any> {
    return this.httpClient.get(`${this.baseUrl}/wallet?isTopup=true`);
  }

  addMoney(walletData: AddMoneyPayload): Observable<any> {
    let payload = {
      ...walletData,
      countryCode: this.userCountryService._countryCode(),
      deviceType: AppConstant.DEVICE_TYPE,
    };

    // Route to Razorpay endpoint when active
    if (this.paymentGatewayService.isRazorpay) {
      const isSeason =
        walletData.contentType &&
        [ContentType.WEB_SERIES, ContentType.MICRO_SERIES].includes(
          walletData.contentType,
        );
      const contentId = isSeason ? walletData.seasonId : walletData.movieId;

      // Content purchase flow (has movieId or seasonId)
      if (contentId) {
        const orderPayload: any = {
          contentType: walletData.contentType ?? ContentType.FILM,
          amount: walletData.amount,
          walletAmount: walletData.walletAmount ?? 0,
          rewardAmount: walletData.rewardAmount ?? 0,
          countryCode: this.userCountryService._countryCode(),
          deviceType: AppConstant.DEVICE_TYPE,
        };
        if (isSeason && walletData.seasonId) {
          orderPayload.seasonId = walletData.seasonId;
        }
        if (walletData.couponCode) {
          orderPayload.couponCode = walletData.couponCode;
          orderPayload.couponType = walletData.couponType;
        }
        return this.httpClient
          .post(
            `${this.baseUrl}/wallet/razorpay/order/${contentId}`,
            orderPayload,
          )
          .pipe(tap(() => this.updateTransaction(Math.random())));
      }

      // Standalone wallet recharge (no content)
      const rechargePayload = {
        amount: walletData.amount,
        countryCode: this.userCountryService._countryCode(),
        deviceType: AppConstant.DEVICE_TYPE,
      };
      let url = `${this.baseUrl}/wallet/razorpay/addMoney`;
      if (walletData.couponCode && walletData.couponType) {
        url = `${url}?couponCode=${walletData.couponCode}&couponType=${walletData.couponType}`;
      }
      return this.httpClient
        .post(url, rechargePayload)
        .pipe(tap(() => this.updateTransaction(Math.random())));
    }

    let url = `${this.baseUrl}/wallet/addmoney`;
    if (walletData.couponCode && walletData.couponType) {
      url = `${url}?couponCode=${walletData.couponCode}&couponType=${walletData.couponType}`;
    }
    return this.httpClient
      .post(url, payload)
      .pipe(tap(() => this.updateTransaction(Math.random())));
  }

  WalletPay({
    filmId,
    seasonId,
    app_id,
    rewardAmount,
    walletAmount,
    couponCode,
    couponType,
    contentType = ContentType.FILM,
  }: WalletPayPayload): Observable<ApiResponse<MoviePurchaseResponse>> {
    const isSeason = [
      ContentType.WEB_SERIES,
      ContentType.MICRO_SERIES,
    ].includes(contentType);

    const contentId = isSeason ? seasonId : filmId;

    // Route to Razorpay endpoint when active
    if (this.paymentGatewayService.isRazorpay) {
      const url = `${this.baseUrl}/wallet/razorpay/order/${contentId}`;
      const payload: any = {
        contentType,
        amount: 0,
        walletAmount,
        rewardAmount,
        countryCode: this.userCountryService._countryCode(),
        deviceType: AppConstant.DEVICE_TYPE,
      };
      if (isSeason && seasonId) {
        payload.seasonId = seasonId;
      }
      if (app_id) {
        payload.app_id = app_id;
      }
      if (couponCode) {
        payload.couponCode = couponCode;
        payload.couponType = couponType;
      }
      return this.httpClient
        .post<ApiResponse<MoviePurchaseResponse>>(url, payload)
        .pipe(tap(() => this.updateTransaction(Math.random())));
    }

    let url = `${this.baseUrl}/wallet/order/${contentId}?walletAmount=${walletAmount}&rewardAmount=${rewardAmount}&contentType=${contentType}`;
    url = app_id ? `${url}&app_id=${app_id}` : url;
    if (couponCode) {
      url = `${url}&couponCode=${couponCode}&couponType=${couponType}`;
    }
    return this.httpClient
      .get<ApiResponse<MoviePurchaseResponse>>(url)
      .pipe(tap(() => this.updateTransaction(Math.random())));
  }

  // (Remove if not needed) Not used
  getTransactionList(): Observable<any> {
    return this.httpClient.get(`${this.baseUrl}/wallet/transactions`);
  }

  getFilteredTransaction(
    type: WalletType,
    page: number,
    limit: number,
    sortOrder: 'desc' | 'asc',
    key: string,
    matchParams: string,
  ): Observable<ApiResponse<any>> {
    return this.httpClient.get<ApiResponse<any>>(
      `${this.baseUrl}/wallet/reward/transactions?type=${type}&page=${page}&limit=${limit}&sortOrder=${sortOrder}&key=${key}&matchParams=${matchParams}`,
    );
  }

  getCelebrationFlag(): boolean {
    return this.localStorageService.get(this.CELEBRATION_FLAG) as boolean;
  }

  setCelebrationFlag(flag: boolean) {
    this.localStorageService.set(this.CELEBRATION_FLAG, flag);
  }

  removeCelebrationFlag() {
    this.localStorageService.remove(this.CELEBRATION_FLAG);
  }

  private transactionSource = new BehaviorSubject<number>(0);

  // Observable that sibling components will subscribe to
  currentTransaction = this.transactionSource.asObservable();

  updateTransaction(transaction: number) {
    this.transactionSource.next(transaction);
  }

  juryFeePayment(paymentData: any): Observable<any> {
    let payload = {
      ...paymentData,
      countryCode: this.userCountryService._countryCode(),
      deviceType: AppConstant.DEVICE_TYPE,
    };
    return this.httpClient
      .post(`${this.baseUrl}/wallet/depositeFilmMakerMoney`, payload)
      .pipe(tap(() => this.updateTransaction(Math.random())));
  }
}
